/**
 * useRefreshBadge — Hook for live update badge functionality
 *
 * Provides:
 * - triggerRefresh(): walks component tree to find data components in scope,
 *   calls each one's refetch() from the registry
 * - lastRefreshed: most recent timestamp among scoped data components
 * - formattedTime: human-readable relative time ("2 minutes ago", "Just now")
 * - isRefreshing: whether a scoped refresh is in progress
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ComponentType } from "@/types";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import { getComponentDescendants } from "@/utils/componentHierarchy";
import {
  getRefetch,
  useRefreshStore,
} from "@/stores/data/refresh-store";

/** Component types that have data-fetching hooks */
const DATA_COMPONENT_TYPES: string[] = [
  ComponentType.DATA_TEXT,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.DATA_CARD,
  ComponentType.LINE_CHART,
  ComponentType.BAR_CHART,
  ComponentType.PIE_CHART,
  ComponentType.AREA_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.MAP_CHART,
  ComponentType.TABLE,
  ComponentType.ADVANCED_TABLE,
];

export interface UseRefreshBadgeOptions {
  scopeContainerId: string | undefined;
  enabled?: boolean;
}

export interface UseRefreshBadgeResult {
  triggerRefresh: () => void;
  lastRefreshed: Date | null;
  isRefreshing: boolean;
  formattedTime: string;
}

export function useRefreshBadge({
  scopeContainerId,
  enabled = true,
}: UseRefreshBadgeOptions): UseRefreshBadgeResult {
  const [tick, setTick] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Get components from whichever store has them (editor or viewer)
  // NOTE: viewerStore.components only contains root-level components (children filtered out).
  // Use componentMap which includes ALL components including nested children.
  const editorComponents = useEditorStore((s) => s.components);
  const viewerComponentMap = useViewerStore((s) => s.componentMap);

  // Build flat array from viewer's componentMap (includes ALL nested children)
  const viewerComponents = useMemo(
    () => viewerComponentMap.size > 0 ? Array.from(viewerComponentMap.values()) : [],
    [viewerComponentMap],
  );

  // Select store based on where the scope container actually exists
  const components = useMemo(() => {
    if (!scopeContainerId) {
      return viewerComponents.length > 0 ? viewerComponents : editorComponents;
    }
    if (viewerComponentMap.has(scopeContainerId)) return viewerComponents;
    return editorComponents;
  }, [scopeContainerId, editorComponents, viewerComponents, viewerComponentMap]);

  // Read timestamps from store
  const componentTimestamps = useRefreshStore((s) => s.componentTimestamps);
  const scopeRefreshing = useRefreshStore(
    (s) => (scopeContainerId ? s.scopeRefreshing[scopeContainerId] ?? false : false),
  );

  // Find real data component IDs within a container's descendants
  // (excludes timestamp-aggregate which is the badge's own display text)
  const findDataIds = useCallback((containerId: string): string[] => {
    const descendants = getComponentDescendants(containerId, components);
    return descendants
      .filter((c) => DATA_COMPONENT_TYPES.includes(c.type))
      .filter((c) => {
        const ds = (c.properties as Record<string, unknown>)?.dataSource as
          { type?: string } | undefined;
        return ds?.type !== 'timestamp-aggregate';
      })
      .map((c) => c.id);
  }, [components]);

  // Find scoped data component IDs — walks up the tree if the stored scope
  // is too narrow (e.g., badge set before data components were added, or
  // data components live in sibling/ancestor containers)
  const scopedDataComponentIds = useMemo(() => {
    if (!enabled || !scopeContainerId) return [];

    // Try the stored scope first
    const ids = findDataIds(scopeContainerId);
    if (ids.length > 0) return ids;

    // Walk up parent chain to find a broader scope with real data components
    let current = components.find((c) => c.id === scopeContainerId);
    while (current?.parentId) {
      const parentIds = findDataIds(current.parentId);
      if (parentIds.length > 0) return parentIds;
      current = components.find((c) => c.id === current!.parentId);
    }

    return [];
  }, [enabled, scopeContainerId, components, findDataIds]);

  // Compute most recent timestamp among scoped components
  const lastRefreshed = useMemo(() => {
    if (scopedDataComponentIds.length === 0) return null;
    let latest = 0;
    for (const id of scopedDataComponentIds) {
      const ts = componentTimestamps[id];
      if (ts && ts > latest) latest = ts;
    }
    return latest > 0 ? new Date(latest) : null;
  }, [scopedDataComponentIds, componentTimestamps]);

  // Format the time string, re-evaluated every tick
  const formattedTime = useMemo(() => {
    // Reference tick so this recomputes on interval
    void tick;
    if (scopedDataComponentIds.length === 0) return "No data";
    if (!lastRefreshed) return "Awaiting data";
    const seconds = Math.floor((Date.now() - lastRefreshed.getTime()) / 1000);
    if (seconds < 5) return "Just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return formatDistanceToNow(lastRefreshed, { addSuffix: true });
  }, [lastRefreshed, tick, scopedDataComponentIds.length]);

  // Tick every 5s while recent (<2min), then every 30s
  useEffect(() => {
    if (!enabled) return;
    const isRecent = lastRefreshed && (Date.now() - lastRefreshed.getTime()) < 120_000;
    const interval = isRecent ? 5_000 : 30_000;
    intervalRef.current = setInterval(() => setTick((t) => t + 1), interval);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, lastRefreshed]);

  // Trigger refresh: call refetch on all scoped data components
  const triggerRefresh = useCallback(() => {
    if (!enabled || !scopeContainerId || scopeRefreshing) return;

    const { setScopeRefreshing } = useRefreshStore.getState();
    setScopeRefreshing(scopeContainerId, true);

    const promises: Promise<void>[] = [];
    for (const id of scopedDataComponentIds) {
      const refetch = getRefetch(id);
      if (refetch) promises.push(refetch());
    }

    // Minimum 600ms loading animation for visible feedback
    const minDelay = new Promise<void>(resolve => setTimeout(resolve, 600));

    Promise.allSettled([...promises, minDelay]).finally(() => {
      setScopeRefreshing(scopeContainerId, false);
      // Force a tick to update the displayed time immediately
      setTick((t) => t + 1);
    });
  }, [enabled, scopeContainerId, scopeRefreshing, scopedDataComponentIds]);

  return {
    triggerRefresh,
    lastRefreshed,
    isRefreshing: scopeRefreshing,
    formattedTime,
  };
}
